import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  model,
  output,
  signal,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import {
  YdsButtonComponent,
  YdsComboboxComponent,
  YdsModalComponent,
  YdsTextFieldComponent,
  type YdsComboboxOption,
} from '../../../yds-angular';
import { isInvalid } from '../../../shared/general-ledger-yds.utils';
import { SelectionCodeOption } from '../../../model/export-rule.model';
import { TenantInfo } from '../../../model/dashboard.model';
import { DashboardService } from '../../../home/dashboard.service';
import { ExportDefinitionService } from '../export-definition.service';

const MAX_NAME_LENGTH = 50;

/**
 * Separator for the sequence number appended to a source option id. Several system
 * supplied definitions share the empty guid, so the raw code is not unique enough
 * for the combobox; the number is stripped again before the code is sent.
 */
const SOURCE_ID_SEPARATOR = ';';

/**
 * "Kopiëren Exportdefinitie" popup. Copies an existing export definition under a
 * new name and emits {@link copied} so the caller can refresh its list.
 */
@Component({
  selector: 'app-exportdefinitioncopy-dialog',
  imports: [
    ReactiveFormsModule,
    YdsModalComponent,
    YdsButtonComponent,
    YdsComboboxComponent,
    YdsTextFieldComponent,
  ],
  templateUrl: './exportdefinitioncopy-dialog.component.html',
  styleUrl: './exportdefinitioncopy-dialog.component.scss',
})
export class ExportdefinitioncopyDialogComponent {
  readonly isInvalid = isInvalid;
  readonly maxNameLength = MAX_NAME_LENGTH;

  /** Two-way open state so the caller can show/hide the popup. */
  readonly open = model<boolean>(false);

  /** Emits the id of the new definition after a successful copy. */
  readonly copied = output<string>();

  private formBuilder = inject(FormBuilder);
  private exportDefinitionService = inject(ExportDefinitionService);
  private dashboardService = inject(DashboardService);
  private destroyRef = inject(DestroyRef);

  readonly sourceOptions = signal<SelectionCodeOption[]>([]);
  readonly tenants = signal<TenantInfo[]>([]);
  readonly isSaving = signal(false);

  /** Set when the backend reports the new name is already in use (HTTP 409). */
  readonly nameTaken = signal(false);

  private readonly newNameValue = signal('');
  private readonly sourceCodeValue = signal('');

  readonly form: FormGroup = this.formBuilder.group({
    sourceCode: ['', Validators.required],
    newName: ['', [Validators.required, Validators.maxLength(MAX_NAME_LENGTH)]],
    // Shown for information only; the copy is always created in the current tenant.
    tenantCode: [{ value: '', disabled: true }],
  });

  readonly sourceComboOptions = computed<YdsComboboxOption[]>(() =>
    this.sourceOptions().map((option, index) => ({
      id: `${option.code}${SOURCE_ID_SEPARATOR}${index}`,
      label: option.description || option.code,
    })),
  );

  readonly tenantComboOptions = computed<YdsComboboxOption[]>(() =>
    this.tenants().map((tenant) => ({
      id: String(tenant.code),
      label: tenant.name,
    })),
  );

  readonly canCopy = computed(
    () =>
      !this.isSaving() &&
      !this.nameTaken() &&
      this.sourceCodeValue().length > 0 &&
      this.newNameValue().length > 0,
  );

  constructor() {
    this.exportDefinitionService
      .getAllExportDefinitionOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options) => {
        this.sourceOptions.set(options);
        if (!this.form.get('sourceCode')?.value) {
          this.selectFirstSource();
        }
      });

    this.dashboardService
      .getTenantInfo()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tenants) => {
        this.tenants.set(tenants);
        if (tenants.length > 0 && !this.form.get('tenantCode')?.value) {
          this.form.patchValue({ tenantCode: String(tenants[0].code) });
        }
      });

    // Suggest "<naam> (kopie)" whenever another source definition is picked.
    this.form
      .get('sourceCode')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string | null) => {
        this.sourceCodeValue.set(value ?? '');
        const description = this.sourceOptionFor(value)?.description;
        this.form.patchValue({
          newName: description ? `${description} (kopie)`.slice(0, MAX_NAME_LENGTH) : '',
        });
      });

    const newName$ = this.form.get('newName')!.valueChanges;

    newName$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((name: string | null) => {
      this.newNameValue.set((name ?? '').trim());
      this.nameTaken.set(false);
    });

    newName$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((name: string | null) => {
          const trimmed = (name ?? '').trim();
          return trimmed
            ? this.exportDefinitionService.exportDefinitionNameExists(trimmed)
            : of(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((exists) => this.nameTaken.set(exists));

    effect(() => {
      if (this.open()) {
        this.resetForm();
      }
    });
  }

  onCancel(): void {
    this.open.set(false);
  }

  onCopy(): void {
    if (!this.canCopy()) {
      return;
    }

    const source = this.sourceOptionFor(this.form.get('sourceCode')?.value as string | null);
    if (!source) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);

    this.exportDefinitionService
      .copyExportDefinition({
        payrollClientId: null,
        institutionId: null,
        exportDescription_Name: source.description,
        exportDefinitionId: source.code,
        newName: this.newNameValue(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (newId) => {
          this.isSaving.set(false);
          this.open.set(false);
          this.copied.emit(newId);
        },
        error: (err: HttpErrorResponse) => {
          this.isSaving.set(false);
          if (err.status === 409) {
            this.nameTaken.set(true);
          }
        },
      });
  }

  private resetForm(): void {
    const firstTenant = this.tenants()[0];
    this.form.reset({
      sourceCode: '',
      newName: '',
      tenantCode: firstTenant ? String(firstTenant.code) : '',
    });
    this.newNameValue.set('');
    this.sourceCodeValue.set('');
    this.nameTaken.set(false);
    // Patched after the reset so the suggested name is not cleared again.
    this.selectFirstSource();
  }

  private selectFirstSource(): void {
    const first = this.sourceComboOptions()[0];
    if (first) {
      this.form.patchValue({ sourceCode: first.id });
    }
  }

  /** Resolves a combobox id ('<code>;<number>') back to its selection list entry. */
  private sourceOptionFor(value: string | null | undefined): SelectionCodeOption | undefined {
    if (!value) {
      return undefined;
    }
    const separatorAt = value.lastIndexOf(SOURCE_ID_SEPARATOR);
    const index = separatorAt < 0 ? NaN : Number(value.slice(separatorAt + 1));
    return Number.isInteger(index) ? this.sourceOptions()[index] : undefined;
  }
}
